<script lang="ts">
  import { getSubcategoryNameByIndex } from '$lib/er/item/translation';
  import LL, { locale } from '$i18n/i18n-svelte';
  import type ERAR from '$lib/erar/spec';
  import { isAfter1_0ByVersion, isNormal } from '$lib/er/season/season';
  import { getTranslatedCharacterName } from '$lib/er/character/translations';

  import {
    matchStatisticsContext,
    type MatchStatisticsContext,
  } from './match-details-content.svelte';
  import { getContext } from 'svelte';
  import { indexToSubcategory } from '$lib/er/item/stores';
  import Meter from '$components/meter.svelte';
  import RawInfusionImage from '$lib/er/infusion/raw-infusion-image.svelte';
  import CharacterIcon from '../character/character-icon.svelte';
  import RawWeaponImage from '../item/raw-weapon-image.svelte';
  import { CharacterImageType } from '../character/consts';
  import Equipment from '../item/equipment.svelte';
  import { getRankTier, getRankTierByQueue } from '../rank-tiers/calculation';
  import RankTierText from '../rank-tiers/rank-tier-text.svelte';
  import RawTraitImage from '../trait/raw-trait-image.svelte';
  import RawVskillImage from '../vskill/raw-vskill-image.svelte';

  export let match: ERAR.Schema.MatchParticipation;

  const { largestDeal, queueTable } = getContext<MatchStatisticsContext>(matchStatisticsContext);

  $: weaponSubcategoryId = $indexToSubcategory.get(match.itemSubcategoryIndex)?.id;
</script>

<li class="match-details-player">
  <div class="character-container">
    <CharacterIcon
      id={match.characterId}
      skinIndex={match.characterSkinIndex}
      type={CharacterImageType.Lobby}
    >
      <svelte:fragment slot="level">{match.level}</svelte:fragment>
      <svelte:fragment slot="weapon">
        {#if weaponSubcategoryId}
          <RawWeaponImage {weaponSubcategoryId} />
        {/if}
      </svelte:fragment>
    </CharacterIcon>
    <span class="weapon-character-name"
      >{getSubcategoryNameByIndex($LL, match.itemSubcategoryIndex)}
      {getTranslatedCharacterName($LL, match.characterId)}</span
    >
  </div>
  <section class="stats">
    <div class="name">
      <h3><a href="/{$locale}/players/{match.recordedName}">{match.recordedName}</a></h3>
      <RankTierText
        rankTier={isNormal(match.seasonId)
          ? getRankTierByQueue({
              ...queueTable[match.playerId],
              seasonId: isAfter1_0ByVersion(match.matchVersion) ? 19 : 1,
            })
          : getRankTier({
              mmr: match.afterMMR ?? match.recordedMMR,
              seasonId: match.seasonId,
              flags: match.recordedMMRFlags,
            })}
        seasonId={isNormal(match.seasonId)
          ? isAfter1_0ByVersion(match.matchVersion)
            ? 19
            : 1
          : match.seasonId}
        shortText={matchMedia('(max-width: 520px)').matches}
        recordedMMR={match.recordedMMR}
      />
      <section class="badges">
        <div>{match.activityFlags.premade}인큐</div>
        {#if match.activityFlags.killedWickline}
          <div>{$LL.players.detail.killedWickline()}</div>
        {/if}
        {#if match.activityFlags.killedAlpha}
          <div>{$LL.players.detail.killedAlpha()}</div>
        {/if}
        {#if match.activityFlags.killedOmega}
          <div>{$LL.players.detail.killedOmega()}</div>
        {/if}
        {#if match.activityFlags.deadByWildAnimal}
          <div>{$LL.players.detail.deadByWildAnimal()}</div>
        {/if}
        {#if match.activityFlags.gaveUp}
          <div>{$LL.players.detail.gaveUp()}</div>
        {/if}
      </section>
    </div>
    <div class="stat kda">
      <h3>K·D·A</h3>
      <div class="kda-content">
        <span class="kill">{match.kill}</span><span class="death">{match.death}</span><span
          >{match.assist}</span
        >
      </div>
    </div>
    <div class="deal">
      <Meter value={match.totalDeal} max={largestDeal} highest={largestDeal} />
      <span>
        {match.totalDeal}
      </span>
    </div>
  </section>
  <section class="items">
    {#if match.preferences.traits}
      <section class="traits">
        <RawTraitImage width="22" traitId={match.preferences.traits.firstCore} />
        {#if match.preferences.traits.secondSub.length > 0}
          {#if match.queueType === '4'}
            <RawInfusionImage width="22" infusionId={match.preferences.traits.secondSub[0]} />
          {:else}
            <RawTraitImage width="22" traitId={match.preferences.traits.secondSub[0]} asCategory />
          {/if}
        {/if}
        {#if match.spellId}
          <div class="vskill">
            <RawVskillImage width="22" skillId={match.spellId} />
            <span class="vskill-level">{match.spellLevel ?? '?'}</span>
          </div>
        {/if}
      </section>
    {/if}
    <Equipment itemIds={match.preferences.equipments} size={3} />
  </section>
</li>

<style lang="postcss">
  .match-details-player {
    padding: var(--space-3);
    display: grid;
    grid-template:
      'meta  meta' auto
      'image stats' auto
      'items items' auto
      'ids   ids  ' auto
      / 5.4rem 1fr;

    &:not(:last-child) {
      border-bottom: 1px solid var(--border-normal);
    }

    :global(.rp-tier-text) {
      font-size: var(--size-4);
      :global(img) {
        height: 1.4rem;
      }
    }
  }
  .character-container {
    grid-area: image;
    margin-left: 0.4rem;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .stats {
    grid-area: stats;
    display: grid;
    grid-template-columns: 1fr 6.4rem 4rem;
    align-items: center;
    gap: var(--space-4);
    margin-left: var(--space-4);
    justify-self: stretch;
  }
  .stat {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;

    h3 {
      font-weight: var(--weights-normal);
      padding: 0;
      margin: 0;
    }

    span {
      font-size: var(--size-5);
      font-weight: var(--weights-bold);
    }

    &.kda {
      margin-right: var(--space-5);
      margin-left: var(--space-5);
    }
    .kda-content {
      display: flex;

      span:not(:last-child)::after {
        content: '·';
        color: var(--mauve-8);
        margin: 0 var(--space-1);
      }
    }
  }

  .deal {
    display: flex;
    flex-direction: column;
    font-size: var(--size-5);

    :global(.meter) {
      margin-bottom: var(--space-3);
    }
  }

  .name {
    font-size: var(--size-5);
    margin-left: var(--space-4);

    h3 {
      word-break: break-all;
    }
  }

  .items {
    grid-area: items;
    display: flex;
    gap: var(--space-3);
    align-items: center;
  }

  .traits {
    height: 3rem;
    display: flex;
    gap: var(--space-2);
    padding: var(--space-2);
    background-color: var(--elevation-2);
    border-radius: var(--radius-full);
  }

  .weapon-character-name {
    text-align: center;
  }

  .vskill {
    position: relative;

    .vskill-level {
      position: absolute;
      right: -2px;
      bottom: -4px;
      padding: 1px var(--space-1);
      border-radius: var(--radius-full);
      background-color: var(--elevation-2);
    }
  }

  .death {
    color: var(--death);
  }

  .badges {
    display: flex;
    font-size: var(--size-4);

    div:not(:last-child)::after {
      content: '·';
      color: var(--mauve-8);
      margin: 0 var(--space-1);
    }
  }
</style>
